// react
import { useEffect, useRef } from 'react';

// capacitor
import {
  App as CapacitorApp,
  AppLaunchUrl,
  URLOpenListenerEvent,
} from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';

// sentry
import * as Sentry from '@sentry/react';
import { useOpenUrl } from 'hooks/useOpenUrl';

/**
 * UniversalLinkHandler listens for universal links and navigates to the
 * appropriate path.
 */
export const useUniversalLinkHandler = () => {
  // refs
  const listenerRef = useRef<PluginListenerHandle | null>(null);
  const appUrlOpenRef = useRef(false);

  // hooks
  const { openUrl, getIsRefresh } = useOpenUrl();

  // effects
  const shutdown = useEffect(() => {
    let cancelled = false;

    // setup listener for appUrlOpen
    const setupListener = async () => {
      const handle = await CapacitorApp.addListener(
        'appUrlOpen',
        (data: URLOpenListenerEvent) => {
          if (cancelled) return;
          if (appUrlOpenRef.current) {
            // Without the query, which can hold an auth code
            const url = data.url.split('?')[0];
            console.warn(
              '[useUniversalLinkHandler] dropped appUrlOpen while handling another:',
              url,
            );
            Sentry.addBreadcrumb({
              category: 'deeplink',
              message: 'appUrlOpen dropped while handling another',
              level: 'warning',
              data: { url },
            });
            return;
          }
          appUrlOpenRef.current = true;

          console.log('handling appUrlOpen', data);
          Promise.resolve(openUrl(data.url))
            .catch((e) => {
              console.error('[useUniversalLinkHandler] openURL error:', e);
              Sentry.captureException(e, {
                tags: { 'deeplink.action': 'open' },
              });
            })
            .finally(() => {
              appUrlOpenRef.current = false;
            });
        },
      );

      if (!cancelled) {
        listenerRef.current = handle;
      } else {
        // if we’ve already unmounted by the time the listener was ready
        handle.remove();
      }
    };

    // initialize the listener then check for launch URL
    const init = async () => {
      await setupListener();

      if (cancelled || appUrlOpenRef.current) return;
      appUrlOpenRef.current = true;

      try {
        const data: AppLaunchUrl | undefined =
          await CapacitorApp.getLaunchUrl();
        if (cancelled || !data?.url || getIsRefresh(data.url)) return;

        console.log('handling launch url', data.url);
        await openUrl(data.url);
      } finally {
        appUrlOpenRef.current = false;
      }
    };

    // microtask so React finishes commit before we register/listen
    Promise.resolve().then(init);

    return () => {
      cancelled = true;
      if (listenerRef.current) {
        listenerRef.current.remove();
      }
    };
  }, [openUrl, getIsRefresh]);

  return { shutdown };
};

export default useUniversalLinkHandler;
